import { Link, useSearchParams } from 'react-router-dom';
import { mexicanRestaurants } from '../../mocks/restaurants';
import { useState, useEffect } from 'react';
import { useSEO } from '../../utils/seo';

export default function MexicanRestaurants() {
  const [searchParams] = useSearchParams();
  const [startIndex, setStartIndex] = useState(0);
  const [glowingRestaurant, setGlowingRestaurant] = useState<number | null>(null);
  const itemsPerPage = 6;
  const displayedRestaurants = mexicanRestaurants.slice(startIndex, startIndex + itemsPerPage);
  const hasMore = startIndex + itemsPerPage < mexicanRestaurants.length;

  useSEO({
    title: 'Best Mexican Restaurants Antalya Authentic Tacos Burritos Guide',
    description: 'Discover the best Mexican restaurants in Antalya serving authentic tacos, burritos, enchiladas, and margaritas. Find top-rated Mexican restaurants in Antalya with reviews, ratings, locations, and contact information for an unforgettable dining experience.',
    keywords: 'Mexican restaurants Antalya, tacos Antalya, Mexican food',
    canonical: '/mexican-restaurants',
    ogTitle: 'Best Mexican Restaurants in Antalya',
    ogDescription: 'Taste authentic Mexican flavors in Antalya',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Mexican Restaurants in Antalya',
      'url': '{{SITE_URL}}/mexican-restaurants',
      'description': 'Find the best Mexican restaurants in Antalya',
      'mainEntity': {
        '@type': 'ItemList',
        'itemListElement': mexicanRestaurants.map((restaurant, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'item': {
            '@type': 'Restaurant',
            'name': restaurant.name,
            'description': restaurant.description,
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': restaurant.address,
              'addressLocality': 'Antalya',
              'addressCountry': 'TR'
            },
            'telephone': restaurant.phone,
            'priceRange': restaurant.priceRange,
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': restaurant.rating,
              'bestRating': '5'
            },
            'servesCuisine': 'Mexican'
          }
        }))
      }
    }
  });

  useEffect(() => {
    const restaurantId = searchParams.get('restaurant');
    if (restaurantId) {
      const id = parseInt(restaurantId);
      setGlowingRestaurant(id);
      
      // Scroll to the restaurant
      setTimeout(() => {
        const element = document.getElementById(`restaurant-${id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Remove glow after 2 seconds
      setTimeout(() => {
        setGlowingRestaurant(null);
      }, 2000);
    }
  }, [searchParams]);

  const handleLoadMore = () => {
    if (hasMore) {
      setStartIndex(prev => prev + itemsPerPage);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/46496767-ecf9-45a1-9f97-898f0428276b.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-lg font-bold tracking-tight">ANTALYA EATS</span>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <section className="pt-20 pb-8 px-4 bg-green-500 border-b-4 border-black">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-fire-line text-5xl text-white"></i>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">MEXICAN RESTAURANTS</h1>
          <p className="text-base text-white">Taste <strong>authentic Mexican flavors</strong> in Antalya</p>
        </div>
      </section>

      {/* Restaurants List */}
      <main>
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid gap-4">
            {mexicanRestaurants.map((restaurant) => (
              <article 
                key={restaurant.id}
                id={`restaurant-${restaurant.id}`}
                className={`bg-white border-4 border-black p-4 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all ${
                  glowingRestaurant === restaurant.id ? 'animate-glow' : ''
                }`}
                style={glowingRestaurant === restaurant.id ? {
                  boxShadow: '0 0 20px 5px rgba(34, 197, 94, 0.8), 8px 8px 0px 0px rgba(0,0,0,1)',
                  animation: 'glow 0.5s ease-in-out 4'
                } : undefined}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{restaurant.name}</h2>
                    <p className="text-gray-600 text-sm mb-3">{restaurant.description}</p>
                    <div className="flex flex-col gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <i className="ri-map-pin-line text-base"></i>
                        <span>{restaurant.address}</span>
                      </div>
                      <a href={`tel:${restaurant.phone}`} className="flex items-center gap-2 hover:text-green-600 transition-colors cursor-pointer">
                        <i className="ri-phone-line text-base"></i>
                        <span>{restaurant.phone}</span>
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-lg"></i>
                      <span className="text-lg font-bold">{restaurant.rating}</span>
                    </div>
                    <div className="text-base font-bold text-green-600">{restaurant.priceRange}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex justify-center gap-4">
            <Link 
              to="/"
              className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
            >
              ← BACK TO CATEGORIES
            </Link>
            {hasMore && (
              <button
                onClick={handleLoadMore}
                className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
              >
                MORE →
              </button>
            )}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Powered by Readdy</a>
        </div>
      </footer>
    </div>
  );
}
